export type SuggestionGroupKey = 'status' | 'location' | 'licenseType' | 'department' | 'people' | 'goTo';

/** One offerable query — the exact string shown AND the exact string the parser runs on
    selection. This identity (not a separate pre-built filter spec) is what makes the round-trip
    test suite (Task 4) exhaustive: a suggestion IS a query, nothing more.

    Exactly one of `label`/`labelKey` is set (review fix round 1, Task 16 — matches the
    `titleKey`/`labelKey` naming already used by `DepartmentColumnPopoverEntry`/
    `SharedMeasureEntry` for "this field holds an i18n KEY, not display text"): every group
    except `goTo` sources `label` from `SearchVocabularyStore`'s live data and renders it
    as-is; `goTo`'s two static items (`GO_TO_SUGGESTIONS`) carry `labelKey` instead, resolved
    via `translate` at render (`IntentSearchComponent.suggestionLabel`). */
export interface Suggestion {
  query: string;
  label?: string;
  labelKey?: string;
}

export interface SuggestionGroup {
  key: SuggestionGroupKey;
  items: Suggestion[];
}

/**
 * Per-record counts used to rank suggestions — one entry per DISTINCT vocabulary hit, not per
 * row (400 Kolhapur records still yield ONE 'Kolhapur' entry, counted 400).
 *
 * Keyed by id wherever the source record carries one (location/licenseType/department all do —
 * `customer_branch_id`/`license_type_id`/`department_id` on `LicenseDetail` — matching how
 * `VocabularyEntry.value` is ALSO id-keyed for those three fields), so the builder's
 * cross-reference against the vocabulary is an exact id lookup, not a name/string match.
 *
 * Two exceptions: status has no numeric id on either side (`SearchVocabularyStore`'s status
 * entries are themselves name-keyed, sourced from `LICENSE_STATUS_OPTIONS`'s wire strings), and
 * `performer_name`/`reviewer_name` have no id field on `LicenseDetail` at all, so people are
 * matched by NORMALIZED NAME against the vocabulary's `user` entries — a disclosed, accepted
 * limitation: two different people sharing an exact display name collapse into one suggestion.
 */
export interface UserRecordAggregate {
  statusCounts: Map<string, number>;      // mgr_status string -> record count
  locationCounts: Map<number, number>;    // customer_branch_id -> record count
  licenseTypeCounts: Map<number, number>; // license_type_id -> record count
  departmentCounts: Map<number, number>;  // department_id -> record count
  personNameCounts: Map<string, number>;  // normalizeSearchText(name) -> record count
}

/** Static examples line demonstrating multi-token combination — not personalized, but still a
    real, clickable query (Task 4 verifies every entry resolves through the real parser). */
export const SEARCH_EXAMPLES: readonly string[] = [
  'expired maharashtra',
  'boiler kolhapur',
  'how many pending review',
];

/**
 * Static, non-personalized group — depends on Task 1's route-only-spec fix to resolve at all.
 *
 * Task 16 review fix round 1: these two items carry `labelKey` (never `label`) — the
 * ALREADY-EXISTING `header.intentSearchDestinationWorkspace`/`Reports` keys, resolved via
 * `translate` at render by `IntentSearchComponent.suggestionLabel`, which now branches on
 * `item.labelKey` directly rather than a `groupKey === 'goTo'` special case.
 */
export const GO_TO_SUGGESTIONS: SuggestionGroup = {
  key: 'goTo',
  items: [
    { query: 'workspace', labelKey: 'header.intentSearchDestinationWorkspace' },
    { query: 'reports', labelKey: 'header.intentSearchDestinationReports' },
  ],
};
